import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API, formatApiErrorDetail } from '../context/AuthContext';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Eye,
  Trash,
  DotsThree,
  Megaphone,
  Globe,
  CalendarBlank,
  Play,
  Pause,
  Archive,
  Copy,
  ArrowSquareOut
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const statusColors = {
  draft: 'badge-draft',
  internal_review: 'badge-review',
  scheduled: 'bg-blue-100 text-blue-700',
  published: 'badge-published',
  archived: 'badge-archived',
  disabled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  draft: 'Draft',
  internal_review: 'In Review',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
  disabled: 'Disabled'
};

export function CampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    department_id: '',
    domain_id: '',
    routing_type: 'path'
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [campaignsRes, deptsRes, domainsRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true }),
        axios.get(`${API}/domains`, { withCredentials: true })
      ]);
      setCampaigns(campaignsRes.data);
      setDepartments(deptsRes.data);
      setDomains(domainsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/campaigns`, {
        ...formData,
        form_fields: [],
        page_settings: {
          headline: `Welcome to ${formData.name}`,
          description: formData.description || '',
          button_text: 'Submit',
          success_message: 'Thank you for your submission!'
        }
      }, { withCredentials: true });
      setShowCreateModal(false);
      navigate(`/campaigns/${data.id}/edit`);
    } catch (error) {
      console.error('Failed to create campaign:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (campaignId, action) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/${action}`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error(`Failed to ${action} campaign:`, error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`, { withCredentials: true });
      toast.success('Campaign deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to delete campaign');
    }
  };

  const copyPublicUrl = (url) => {
    navigator.clipboard.writeText(url);
    // You could add a toast notification here
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
                          c.slug.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">Campaigns</h1>
            <p className="text-zinc-500 mt-1">Create and manage your campaigns</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            data-testid="create-campaign-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="campaign-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="internal_review">In Review</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaigns Table */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-zinc-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : filteredCampaigns.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="table-header text-left">Campaign</th>
                    <th className="table-header text-left">Department</th>
                    <th className="table-header text-left">Status</th>
                    <th className="table-header text-right">Submissions</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`campaign-row-${campaign.slug}`}>
                      <td className="py-4 px-4">
                        <div>
                          <Link to={`/campaigns/${campaign.id}/edit`} className="font-medium text-zinc-900 hover:text-[#002FA7]">
                            {campaign.name}
                          </Link>
                          <p className="text-sm text-zinc-500">/{campaign.slug}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-zinc-600">
                        {campaign.department_name}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`${statusColors[campaign.status]} px-2 py-1 rounded-sm text-xs font-medium`}>
                          {statusLabels[campaign.status]}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right text-zinc-600">
                        {campaign.submission_count}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-zinc-100 rounded-sm" data-testid={`campaign-menu-${campaign.slug}`}>
                              <DotsThree className="w-5 h-5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}>
                              <PencilSimple className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/submissions?campaign_id=${campaign.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Submissions
                            </DropdownMenuItem>
                            {campaign.public_url && (
                              <>
                                <DropdownMenuItem onClick={() => copyPublicUrl(campaign.public_url)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Copy Public URL
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(`/c/${campaign.slug}`, '_blank')}>
                                  <ArrowSquareOut className="w-4 h-4 mr-2" />
                                  Open Public Page
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            {campaign.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'publish')}>
                                <Play className="w-4 h-4 mr-2" />
                                Publish
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'published' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'unpublish')}>
                                <Pause className="w-4 h-4 mr-2" />
                                Unpublish
                              </DropdownMenuItem>
                            )}
                            {campaign.status !== 'archived' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(campaign.id, 'archive')}>
                                <Archive className="w-4 h-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(campaign.id)} className="text-red-600">
                              <Trash className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
            <Megaphone className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No campaigns found</p>
            <Button onClick={() => setShowCreateModal(true)} className="btn-primary mt-4">
              Create your first campaign
            </Button>
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="modal-brutalist sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Create Campaign</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="label-uppercase">Campaign Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    })}
                    className="input-swiss"
                    placeholder="e.g., Annual Conference 2025"
                    required
                    data-testid="campaign-name-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">URL Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="input-swiss"
                    placeholder="e.g., annual-conference-2025"
                    required
                    data-testid="campaign-slug-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-swiss"
                    placeholder="Brief description of your campaign..."
                    rows={3}
                    data-testid="campaign-description-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Department</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(v) => setFormData({ ...formData, department_id: v })}
                    required
                  >
                    <SelectTrigger data-testid="campaign-department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">Domain</Label>
                  <Select
                    value={formData.domain_id}
                    onValueChange={(v) => setFormData({ ...formData, domain_id: v })}
                    required
                  >
                    <SelectTrigger data-testid="campaign-domain-select">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map(domain => (
                        <SelectItem key={domain.id} value={domain.id}>{domain.base_domain}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">URL Routing</Label>
                  <Select
                    value={formData.routing_type}
                    onValueChange={(v) => setFormData({ ...formData, routing_type: v })}
                  >
                    <SelectTrigger data-testid="campaign-routing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="path">Path-based (domain.com/slug)</SelectItem>
                      <SelectItem value="subdomain">Subdomain (slug.domain.com)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="campaign-create-submit">
                  {saving ? 'Creating...' : 'Create & Configure'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
